import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC, useContext } from "react";

const enum fields {
    srcType = "srcType",
    url = "url",
    jsScript = "jsScript",
}

interface ScriptWidgetProps extends WidgetProps {
    fields: {
        [fields.srcType]: string;
        [fields.url]: string;
        [fields.jsScript]: string;
    };
}

type Props = ScriptWidgetProps;

const ScriptWidget: FC<Props> = ({ fields }) => {
    const refContainer = React.useRef(null);

    React.useEffect(() => {
        const script = document.createElement("script");
        script.type = "text/javascript";

        if (fields.srcType === "jsURL" && fields.url.length !== 0) {
            script.src = fields.url;
            script.async = true;
            try {
                (refContainer.current as any)?.appendChild(script);
            } catch (e) { console.error(e); }
        }
        else if (fields.srcType === "jsText" && fields.jsScript.length !== 0) {
            script.async = true;
            const code = fields.jsScript;
            try {
                script.appendChild(document.createTextNode(code));
                (refContainer.current as any)?.appendChild(script);
            } catch (e) {
                script.text = code;
                (refContainer.current as any)?.appendChild(script);
            }
        }

        return () => {
            if (fields.srcType === "jsURL" && fields.url.length !== 0) {
                // Check if the script exists before trying to remove it
                const existingScript = document.querySelector(`script[src="${fields.url}"]`);
                if (existingScript) {
                    document.body.removeChild(existingScript);
                }
            }
            (refContainer.current as any)?.removeChild(script);
        };
    }, []);

    return (
        <>
            <div ref={refContainer} />
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Script Widget",
    fieldDefinitions: [
        {
            name: fields.srcType,
            displayName: "Source Type",
            editorTemplate: "RadioButtonsField",
            options: [
                {
                    displayName: "URL",
                    value: "jsURL",
                },
                {
                    displayName: "Java Script Text",
                    value: "jsText",
                },
            ],
            defaultValue: "jsURL",
            fieldType: "General",
            sortOrder: 1,
        },
        {
            name: fields.url,
            displayName: "URL",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "URL",
            sortOrder: 2,
            isVisible: item => item.fields.srcType === "jsURL",
        },
        {
            name: fields.jsScript,
            displayName: "Java Script Text",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Java Script Text",
            sortOrder: 3,
            isVisible: item => item.fields.srcType === "jsText",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ScriptWidget,
    definition,
};

export default widgetModule;
